import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { first, map } from 'rxjs/internal/operators';
import { CountyDTO } from 'src/app/components/shared/models/CountyDTO';
import { CityDTO } from 'src/app/components/shared/models/CityDTO';
import { AddressService } from 'src/app/components/shared/services/address.service';
import Swal from 'sweetalert2';
import { FinalClientService } from 'src/app/components/account/services/final-client.service';
import { OperatorService } from './../../../operator/services/operator.service';
import { PlaceDetailsGas } from "../../../account/models/PlaceDetailsGas";

@Component({
  selector: 'app-gas-consumption-place-details',
  templateUrl: './gas-consumption-place.component.html',
  styleUrls: ['./gas-consumption-place.component.scss']
})
export class GasConsumptionPlaceComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public conventionParts: string[] = [];
  public suppliers: string[] = [];
  private routeDataSub: Subscription;
  public placeId: number;
  public isFormSubmitAttempt = false;
  public placeDetails: PlaceDetailsGas;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];
  public newPlaceDetails = new PlaceDetailsGas();
  public isNetworkTypeDistribution: boolean;
  public isNetworkTypeTransport: boolean;
  public isNetworkTypeCA: boolean;

  @Input()
  public editable: boolean = false;

  @Input()
  set place(place: any) {
    this.form.patchValue(place);
    this.conventionParts = place.conventionParties;
    this.suppliers = place.suppliers;
  }

  @Input()
  public isContractChange: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private finalClientService: FinalClientService,
    private route: ActivatedRoute,
    private router: Router,
    private addressService: AddressService,
    private operatorService: OperatorService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.createConsumptionPlaceForm();
  }

  createConsumptionPlaceForm(): void {
    this.form = this.formBuilder.group({
      operatorName: [{ value: '', disabled: true }],
      code: ['', Validators.required],
      placeType: [''],
      conventionParties: [''],
      suppliers: [''],
      //  address
      streetNumber: ['', Validators.required],
      streetName: ['', Validators.required],
      postalCode: ['', Validators.required],
      block: [''],
      apartment: [''],
      floor: [''],
      county: ['', Validators.required],
      city: ['', Validators.required],
      //convention
      conventionType: [''],
      conventionNumber: [''],
      conventionDate: [''],
      conventionPresent: [''],
      //technical data
      debit: ['', Validators.required],
      pressure: ['', Validators.required],
      maximumPressure: ['', Validators.required],
      minimumPressure: ['', Validators.required],
      minimumTechnicalPressure: ['', Validators.required],
      reservedCapacity: ['', Validators.required],
      smartMeter: ['', Validators.required],
      counterType: ['', Validators.required],
      counterSeries: ['', Validators.required],
      technicalDataStatus: ['', Validators.required],
      networkType: ['', Validators.required],
      counterIndexInterval: ['', Validators.required],
      operatingMode: ['', Validators.required],
      consumptionCategory: ['', Validators.required],
      counterIndexDesc: ['', Validators.required],
      counterIndexValue: [''],
      counterIndexReadDate: [''],
      //network contract
      clientIdentificationNumber: [''],
      clientName: [''],
      contractNumber: [''],
      validFrom: [''],
      validTo: [''],
      contractType: [''],
      //connection certificate
      accessAgreement: [''],
      accessAgreementNumber: [''],
      accessAgreementDate: [''],
      //connection notice
      connectionNotice: [''],
      connectionNoticeNumber: [''],
      connectionNoticeDate: [''],
    });

    this.form.controls['networkType'].valueChanges.subscribe(value => {
      this.isNetworkTypeDistribution = value === 'D' ? true : false;
      this.isNetworkTypeTransport = value === 'T' ? true : false;
      this.isNetworkTypeCA = value === 'CA' ? true : false;
    });
  }

  getFormData(): void {
    if (this.placeId) {
      this.finalClientService.getFCGasConsumptionPlace(this.placeId).pipe(first())
        .subscribe((placeDetailsGas: PlaceDetailsGas) => {
          this.isNetworkTypeDistribution = placeDetailsGas.networkType === 'D'
          this.isNetworkTypeTransport = placeDetailsGas.networkType === 'T'
          this.isNetworkTypeCA = placeDetailsGas.networkType === 'CA'
          this.changeDetectorRef.detectChanges();
          this.form.patchValue(placeDetailsGas);
          this.addressService.getCities(this.form.get('county').value).subscribe
            ((cities: CityDTO[]) => this.cities = cities);

        });
    } else {
      this.operatorService.getOperatorNameConsumptionPlace().pipe(first())
        .subscribe(data => {
          this.form.patchValue(data);
        });
    }
    this.isNetworkTypeDistribution = this.form.get('networkType').value === 'D';
  }

  ngOnInit(): void {
    this.addressService.getCounties().subscribe((counties: CountyDTO[]) => this.counties = counties);
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(() => {
          if (window.history.state.editable) {
            this.editable = !!window.history.state.editable;
            if (!this.editable) {
              this.form.disable();
            }
          } else {
            this.form.disable();
          }
          this.placeId = +window.history.state.placeId;
          return this.placeId;
        })
      ).subscribe(() => this.getFormData());
  }

  setDefaultConsumptionCategory(value: string): void {
    switch (value) {
      case 'D':
        {
          if (!this.form.controls.consumptionCategory.value) {
            this.form.controls.consumptionCategory.setValue("C_1");
          }
        }
      case 'T':
        {
          if (!this.form.controls.consumptionCategory.value) {
            this.form.controls.consumptionCategory.setValue("C_8");
          }
        }
      case 'CA':
        {
          if (!this.form.controls.consumptionCategory.value) {
            this.form.controls.consumptionCategory.setValue("C_8");
          }
        }
    }
  }

  onSubmit(): void {
    const invalidControls = Object.entries(this.form.controls).filter(([key, control]) => {
      return !!control.invalid;
    });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.log('invalidControls', invalidControls);
      this.validationNotification('Vă rugăm să completați toate câmpurile marcate cu asterix');
    } else {
      console.log('controls', this.form.controls);

      this.newPlaceDetails.placeType = 'CLC';
      this.newPlaceDetails.operatorName = this.form.get('operatorName').value;
      this.newPlaceDetails.code = this.form.get('code').value;
      this.newPlaceDetails.county = this.form.get('county').value;
      this.newPlaceDetails.city = this.form.get('city').value;
      this.newPlaceDetails.streetName = this.form.get('streetName').value;
      this.newPlaceDetails.streetNumber = this.form.get('streetNumber').value;
      this.newPlaceDetails.block = this.form.get('block').value;
      this.newPlaceDetails.floor = this.form.get('floor').value;
      this.newPlaceDetails.apartment = this.form.get('apartment').value;
      this.newPlaceDetails.postalCode = this.form.get('postalCode').value;
      this.newPlaceDetails.contractNumber = this.form.get('contractNumber').value;
      this.newPlaceDetails.validFrom = this.form.get('validFrom').value;
      this.newPlaceDetails.conventionNumber = this.form.get('conventionNumber').value;
      this.newPlaceDetails.conventionDate = this.form.get('conventionDate').value;
      this.newPlaceDetails.accessAgreement = this.form.get('accessAgreement').value;
      this.newPlaceDetails.accessAgreementNumber = this.form.get('accessAgreementNumber').value;
      this.newPlaceDetails.accessAgreementDate = this.form.get('accessAgreementDate').value;
      this.newPlaceDetails.connectionNotice = this.form.get('connectionNotice').value;
      this.newPlaceDetails.connectionNoticeNumber = this.form.get('connectionNoticeNumber').value;
      this.newPlaceDetails.connectionNoticeDate = this.form.get('connectionNoticeDate').value;
      this.newPlaceDetails.debit = this.form.get('debit').value;
      this.newPlaceDetails.reservedCapacity = this.form.get('reservedCapacity').value;
      this.newPlaceDetails.pressure = this.form.get('pressure').value;
      this.newPlaceDetails.minimumPressure = this.form.get('minimumPressure').value;
      this.newPlaceDetails.maximumPressure = this.form.get('maximumPressure').value;
      this.newPlaceDetails.minimumTechnicalPressure = this.form.get('minimumTechnicalPressure').value;
      this.newPlaceDetails.counterIndexInterval = this.form.get('counterIndexInterval').value;
      this.newPlaceDetails.consumptionCategory = this.form.get('consumptionCategory').value;
      this.newPlaceDetails.technicalDataStatus = this.form.get('technicalDataStatus').value;
      this.newPlaceDetails.operatingMode = this.form.get('operatingMode').value;
      this.newPlaceDetails.networkType = this.form.get('networkType').value;
      this.newPlaceDetails.smartMeter = this.form.controls.smartMeter.value;
      this.newPlaceDetails.counterType = this.form.get('counterType').value;
      this.newPlaceDetails.counterSeries = this.form.get('counterSeries').value;
      this.newPlaceDetails.counterIndexDesc = this.form.get('counterIndexDesc').value;
      this.newPlaceDetails.counterIndexValue = this.form.get('counterIndexValue').value;
      this.newPlaceDetails.counterIndexReadDate = this.form.get('counterIndexReadDate').value;

      if (this.placeId) {
        Swal.fire({
          title: 'Esti sigur?',
          text: "Esti pe cale sa editezi un loc de consum.",
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Da',
          cancelButtonText: 'Nu',
          customClass: {
            popup: 'popup-z-elevate-24-blue'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            this.newPlaceDetails.id = this.placeId;
            this.operatorService.createGasConsumptionPlace(this.newPlaceDetails, "edit").subscribe((val) => {
              this.successEditNotification();
            }, error => {
              if (error) {
                this.errorNotification(error.error.message);
              }
            });
          }
        })
      } else {
        Swal.fire({
          title: 'Esti sigur?',
          text: "Esti pe cale sa adaugi un nou loc de consum.",
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Da',
          cancelButtonText: 'Nu',
          customClass: {
            popup: 'popup-z-elevate-24-blue'
          }
        }).then(() => {
          this.operatorService.createGasConsumptionPlace(this.newPlaceDetails, "create").subscribe((val) => {
            this.router.navigate(['detalii-loc-consum'], {
              relativeTo: this.route,
              state: { placeId: val.id, editable: false }
            });
            this.successCreateNotification();
          }, error => {
            if (error) {
              this.errorNotification(error.error.message);
            }
          });
        })
      }
    }
  }

  onChangeCounty(): void {
    this.cities = [];
    this.addressService.getCities(this.form.get('county').value).subscribe(cities => this.cities = cities);
  }

  get isConventionPresent(): boolean {
    return this.form.get('conventionPresent').value === null || this.form.get('conventionPresent').value === false;
  }

  get isDirectNetworkContractType(): boolean {
    if (this.suppliers == null) {
      return true;
    } else if (this.suppliers.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  get isConsumptionPlaceConnected(): boolean {
    return this.form.get('technicalDataStatus').value === 'CONECTAT';
  }

  get isSmartMeter(): boolean {
    return this.form.controls.smartMeter.value;
  }

  get isConsumptionPlaceDisconnected(): boolean {
    return this.form.get('technicalDataStatus').value === 'DECONECTAT';
  }

  get isConsumptionPlaceInactive(): boolean {
    return this.form.get('technicalDataStatus').value === 'INACTIV';
  }

  get isOperatingModeSeasonal(): boolean {
    return this.form.get('operatingMode').value === 'SEZONIER';
  }

  get isOperatingModePermanent(): boolean {
    return this.form.get('operatingMode').value === 'PERMANENT';
  }

  setProperty(): string {
    if (this.editable) {
      return "enabled";
    } else {
      return "disabled";
    }
  }

  validationNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  errorNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successCreateNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Locul de consum ' + this.newPlaceDetails.code + ' a fost creat cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successEditNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Locul de consum ' + this.newPlaceDetails.code + ' a fost editat cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
  }

}
